import React, { useState, useRef, useEffect } from "react";
import {
  XMarkIcon,
  CheckCircleIcon,
  XCircleIcon,
  MapPinIcon,
} from "@heroicons/react/24/outline";
import axios from "axios";
import toast, { Toaster } from "react-hot-toast";
import Swal from "sweetalert2";
import loginimg from "../../Assets/Login/Modal.png";
import { uploadToCloudinary } from "../../utils/cloudinary";
import { useTranslation } from "react-i18next";
import { useCart } from "../../utils/CartContext";
import GuestSession from "../../utils/guestSession";

const DEMO_OTP = "111111";

const COUNTRIES = [
  { code: "+91", iso: "IN", flag: "🇮🇳", name: "India" },
  { code: "+1", iso: "US", flag: "🇺🇸", name: "United States" },
  { code: "+44", iso: "GB", flag: "🇬🇧", name: "United Kingdom" },
  { code: "+61", iso: "AU", flag: "🇦🇺", name: "Australia" },
  { code: "+971", iso: "AE", flag: "🇦🇪", name: "UAE" },
  { code: "+65", iso: "SG", flag: "🇸🇬", name: "Singapore" },
  { code: "+60", iso: "MY", flag: "🇲🇾", name: "Malaysia" },
  { code: "+49", iso: "DE", flag: "🇩🇪", name: "Germany" },
  { code: "+33", iso: "FR", flag: "🇫🇷", name: "France" },
  { code: "+81", iso: "JP", flag: "🇯🇵", name: "Japan" },
  { code: "+86", iso: "CN", flag: "🇨🇳", name: "China" },
  { code: "+7", iso: "RU", flag: "🇷🇺", name: "Russia" },
  { code: "+55", iso: "BR", flag: "🇧🇷", name: "Brazil" },
  { code: "+27", iso: "ZA", flag: "🇿🇦", name: "South Africa" },
  { code: "+966", iso: "SA", flag: "🇸🇦", name: "Saudi Arabia" },
  { code: "+92", iso: "PK", flag: "🇵🇰", name: "Pakistan" },
  { code: "+880", iso: "BD", flag: "🇧🇩", name: "Bangladesh" },
  { code: "+94", iso: "LK", flag: "🇱🇰", name: "Sri Lanka" },
  { code: "+977", iso: "NP", flag: "🇳🇵", name: "Nepal" },
  { code: "+1", iso: "CA", flag: "🇨🇦", name: "Canada" },
];

// Phone number max lengths by country ISO
const PHONE_LENGTHS = {
  IN: 10,
  US: 10,
  CA: 10,
  GB: 10,
  AU: 9,
  AE: 9,
  SG: 8,
  MY: 9,
  DE: 11,
  FR: 9,
  JP: 10,
  CN: 11,
  RU: 10,
  BR: 11,
  ZA: 9,
  SA: 9,
  PK: 10,
  BD: 10,
  LK: 9,
  NP: 10,
};

const UserLoginModal = ({ onClose, onLoginSuccess }) => {
  // Steps: "phone" | "otp" | "profile"
  const [step, setStep] = useState("phone");
  const [isLoading, setIsLoading] = useState(false);
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [selectedCountry, setSelectedCountry] = useState(COUNTRIES[0]);
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const [countrySearch, setCountrySearch] = useState("");
  const [isNewUser, setIsNewUser] = useState(false);
  const [loggedInUser, setLoggedInUser] = useState(null);
  const [locationFetching, setLocationFetching] = useState(false);

  const otpRefs = useRef([]);
  const modalRef = useRef(null);
  const countryDropdownRef = useRef(null);
  const { t } = useTranslation();
  
  // Get cart context for merging
  const { mergeCartsAfterLogin, mergeWishlistsAfterLogin, fetchCartCount } = useCart();

  const maxPhoneLength = PHONE_LENGTHS[selectedCountry.iso] || 10;

  const [formData, setFormData] = useState({
    phone: "",
    firstName: "",
    lastName: "",
    email: "",
    gender: "",
    image: null,
    location: "",
  });

  const phoneValid = formData.phone.length === maxPhoneLength;
  const phoneStarted = formData.phone.length > 0;

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (modalRef.current && !modalRef.current.contains(e.target)) onClose?.();
      if (
        countryDropdownRef.current &&
        !countryDropdownRef.current.contains(e.target)
      ) {
        setShowCountryDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [onClose]);

  const filteredCountries = COUNTRIES.filter(
    (c) =>
      c.name.toLowerCase().includes(countrySearch.toLowerCase()) ||
      c.code.includes(countrySearch),
  );

  // Auto-fetch live location
  const fetchLocation = () => {
    if (!navigator.geolocation) {
      toast.error("Geolocation not supported by your browser");
      return;
    }
    setLocationFetching(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude, longitude } = pos.coords;
        try {
          const res = await fetch(
            `https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json`,
          );
          const data = await res.json();
          const addr = data.address;
          const readable = [
            addr.suburb || addr.neighbourhood || addr.village,
            addr.city || addr.town || addr.county,
            addr.state,
            addr.country,
          ]
            .filter(Boolean)
            .join(", ");
          setFormData((prev) => ({ ...prev, location: readable }));
          toast.success("Location fetched!");
        } catch {
          setFormData((prev) => ({
            ...prev,
            location: `${latitude.toFixed(4)}, ${longitude.toFixed(4)}`,
          }));
        }
        setLocationFetching(false);
      },
      (err) => {
        toast.error("Could not fetch location. Please allow location access.");
        setLocationFetching(false);
      },
      { timeout: 10000 },
    );
  };

  // Step 1: Send OTP (Flipkart style — no registration upfront)
  const handlePhoneSubmit = async (e) => {
    e.preventDefault();
    if (!phoneValid) {
      toast.error(`Please enter a valid ${maxPhoneLength}-digit phone number`);
      return;
    }

    setIsLoading(true);
    try {
      const fullPhone = `${selectedCountry.code}${formData.phone}`;
      const { data } = await axios.post(
        `${process.env.REACT_APP_API_URL}/customers/user-login-by-phone`,
        { phone: formData.phone },
      );

      if (data?.success) {
        // Existing user
        setIsNewUser(false);
        toast.success(t("loginModal.otp_sent") || "OTP sent! (Demo: 111111)");
      } else {
        // New user — still send OTP, collect profile after
        setIsNewUser(true);
        toast(
          t("loginModal.new_user_otp") ||
            "New user! OTP sent — complete profile after login.",
        );
      }
      setStep("otp");
      setTimeout(() => otpRefs.current[0]?.focus(), 300);
    } catch (err) {
      // If 404 it's a new user — still proceed to OTP
      if (err?.response?.status === 404) {
        setIsNewUser(true);
        toast("New user detected — verify OTP to continue");
        setStep("otp");
        setTimeout(() => otpRefs.current[0]?.focus(), 300);
      } else {
        toast.error(
          t("loginModal.login_error") || "Error — check your connection",
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Step 2: OTP handlers
  const handleOtpChange = (index, value) => {
    if (value && !/^\d+$/.test(value)) return;
    const newOtp = [...otp];
    newOtp[index] = value.slice(0, 1);
    setOtp(newOtp);
    if (value && index < 5) otpRefs.current[index + 1]?.focus();
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otp[index] && index > 0)
      otpRefs.current[index - 1]?.focus();
  };

  // Step 3: Verify OTP - NOW WITH MERGE FUNCTIONALITY
  const handleOtpSubmit = async (e) => {
    e.preventDefault();
    const enteredOtp = otp.join("");

    if (enteredOtp !== DEMO_OTP) {
      toast.error("Invalid OTP (Demo: 111111)");
      setOtp(["", "", "", "", "", ""]);
      otpRefs.current[0]?.focus();
      return;
    }

    if (isNewUser) {
      // New user: go to profile completion
      toast.success("OTP verified! Complete your profile.");
      setStep("profile");
      // Auto-fetch location for new user
      setTimeout(() => fetchLocation(), 500);
      return;
    }

    // Existing user: login directly
    setIsLoading(true);
    try {
      const { data } = await axios.post(
        `${process.env.REACT_APP_API_URL}/customers/user-login-by-phone`,
        { phone: formData.phone },
      );

      if (data.success) {
        const user = data.data;
        localStorage.setItem("pm-user-data", JSON.stringify(user));
        
        // === GUEST MERGE: Merge guest cart and wishlist into user account ===
        const guestId = GuestSession.getGuestId();
        if (guestId) {
          // Merge carts
          await mergeCartsAfterLogin(user._id, guestId);
          // Merge wishlists
          await mergeWishlistsAfterLogin(user._id, guestId);
          // Clear guest ID after merge
          GuestSession.clearGuestId();
        }
        
        // Refresh cart count
        await fetchCartCount();
        
        onLoginSuccess?.(user);
        await Swal.fire({
          icon: "success",
          title: `Welcome back, ${user.firstName || "User"}!`,
          text: "Your cart and wishlist items have been saved.",
          confirmButtonColor: "#00395F",
        });
        onClose?.();
      } else {
        toast.error("Unable to fetch user data.");
      }
    } catch (err) {
      toast.error("Login failed. Try again.");
    } finally {
      setIsLoading(false);
    }
  };

  // Step 4: Profile completion (new users only) - WITH MERGE FUNCTIONALITY
  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    if (!formData.firstName.trim() || !formData.gender) {
      toast.error("Please fill all required fields");
      return;
    }

    setIsLoading(true);
    try {
      let imageUrl = "";
      if (formData.image) {
        const uploadRes = await uploadToCloudinary(formData.image);
        imageUrl = uploadRes.secure_url;
      }

      const payload = {
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        email: formData.email.trim(),
        gender: formData.gender,
        phone: formData.phone,
        imageUrl: imageUrl || "",
      };

      const { data } = await axios.post(
        `${process.env.REACT_APP_API_URL}/customers/create-user`,
        payload,
      );

      if (data.success) {
        const user = data.data;
        localStorage.setItem("pm-user-data", JSON.stringify(user));
        
        // === GUEST MERGE: Merge guest cart and wishlist into new user account ===
        const guestId = GuestSession.getGuestId();
        if (guestId) {
          // Merge carts
          await mergeCartsAfterLogin(user._id, guestId);
          // Merge wishlists
          await mergeWishlistsAfterLogin(user._id, guestId);
          // Clear guest ID after merge
          GuestSession.clearGuestId();
        }
        
        // Refresh cart count
        await fetchCartCount();
        
        onLoginSuccess?.(user);
        await Swal.fire({
          icon: "success",
          title: `Welcome to Bharat Karigari, ${user.firstName}!`,
          text: "Your cart and wishlist items have been saved.",
          confirmButtonColor: "#00395F",
        });
        onClose?.();
      } else {
        toast.error(data.error || "Registration failed");
      }
    } catch (err) {
      toast.error(err?.response?.data?.error || "Something went wrong");
    } finally {
      setIsLoading(false);
    }
  };

  const inputCls =
    "w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#00395F] focus:border-transparent transition-all bg-gray-50 hover:bg-white";

  const stepLabels = ["Phone", "OTP", "Profile"];
  const stepIndex = step === "phone" ? 0 : step === "otp" ? 1 : 2;

  return (
    <>
      <Toaster position="top-center" />
      <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center p-4 z-[999] backdrop-blur-sm">
        <div
          ref={modalRef}
          className="bg-white rounded-3xl overflow-hidden max-w-4xl w-full shadow-2xl grid md:grid-cols-2"
          style={{ maxHeight: "95vh" }}
        >
          {/*  Left: Image Panel  */}
          <div className="relative hidden md:block">
            <img
              src={loginimg}
              alt="Login Visual"
              className="absolute inset-0 w-full h-full object-cover"
            />
            {/* Overlay gradient */}
            <div className="absolute inset-0 bg-gradient-to-t from-[#00395F]/80 to-transparent" />
            <div className="absolute bottom-8 left-8 right-8 text-white">
              <p className="text-xs uppercase tracking-widest opacity-70 mb-1 font-medium">
                Bharat Karigari
              </p>
              <h2 className="text-3xl font-bold leading-snug">
                Discover Royalty
                <br />
                <span className="opacity-80 text-2xl font-normal">
                  Exclusive Insights Await
                </span>
              </h2>
            </div>
          </div>

          {/*  Right: Form Panel  */}
          <div
            className="flex flex-col relative overflow-y-auto"
            style={{ maxHeight: "95vh" }}
          >
            {/* Close button */}
            <button
              onClick={onClose}
              className="absolute top-4 right-4 z-10 p-1.5 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-500 hover:text-gray-800 transition-all"
              aria-label="Close"
            >
              <XMarkIcon className="h-4 w-4" />
            </button>

            <div className="px-8 pt-10 pb-6 flex-1">
              {/* Step indicator */}
              <div className="flex items-center gap-2 mb-8">
                {stepLabels.map((label, i) => (
                  <React.Fragment key={label}>
                    <div className="flex items-center gap-1.5">
                      <div
                        className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold transition-all ${
                          i < stepIndex
                            ? "bg-[#00395F] text-white"
                            : i === stepIndex
                              ? "bg-[#00395F] text-white ring-4 ring-[#00395F]/20"
                              : "bg-gray-100 text-gray-400"
                        }`}
                      >
                        {i < stepIndex ? "✓" : i + 1}
                      </div>
                      <span
                        className={`text-xs font-medium ${
                          i === stepIndex ? "text-[#00395F]" : "text-gray-400"
                        }`}
                      >
                        {label}
                      </span>
                    </div>
                    {i < stepLabels.length - 1 && (
                      <div
                        className={`flex-1 h-0.5 rounded transition-all ${
                          i < stepIndex ? "bg-[#00395F]" : "bg-gray-200"
                        }`}
                      />
                    )}
                  </React.Fragment>
                ))}
              </div>

              {/*  STEP: PHONE  */}
              {step === "phone" && (
                <form onSubmit={handlePhoneSubmit}>
                  <div className="mb-6">
                    <h2 className="text-2xl font-bold text-gray-900 mb-1">
                      Sign in
                    </h2>
                    <p className="text-sm text-gray-500">
                      Enter your phone number to continue
                    </p>
                    <p className="text-xs text-gray-400 mt-1">
                      Guest items will be saved to your account
                    </p>
                  </div>

                  {/* Country code + Phone */}
                  <div className="mb-4">
                    <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 block">
                      Phone Number
                    </label>
                    <div className="flex gap-2">
                      {/* Country Selector */}
                      <div className="relative" ref={countryDropdownRef}>
                        <button
                          type="button"
                          onClick={() =>
                            setShowCountryDropdown(!showCountryDropdown)
                          }
                          className="flex items-center gap-2 px-3 py-3 border border-gray-200 rounded-xl bg-gray-50 hover:bg-white text-sm font-medium transition-all focus:outline-none focus:ring-2 focus:ring-[#00395F] whitespace-nowrap"
                          style={{ minWidth: "96px" }}
                        >
                          <span className="text-lg leading-none">
                            {selectedCountry.flag}
                          </span>
                          <span className="text-gray-700">
                            {selectedCountry.code}
                          </span>
                          <svg
                            className="w-3 h-3 text-gray-400"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M19 9l-7 7-7-7"
                            />
                          </svg>
                        </button>

                        {showCountryDropdown && (
                          <div className="absolute top-full left-0 mt-1 w-64 bg-white border border-gray-200 rounded-xl shadow-xl z-50 overflow-hidden">
                            <div className="p-2 border-b border-gray-100">
                              <input
                                type="text"
                                placeholder="Search country..."
                                value={countrySearch}
                                onChange={(e) =>
                                  setCountrySearch(e.target.value)
                                }
                                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00395F]"
                                autoFocus
                              />
                            </div>
                            <div className="max-h-52 overflow-y-auto">
                              {filteredCountries.map((c) => (
                                <button
                                  key={`${c.iso}-${c.code}`}
                                  type="button"
                                  onClick={() => {
                                    setSelectedCountry(c);
                                    setShowCountryDropdown(false);
                                    setCountrySearch("");
                                    setFormData((prev) => ({
                                      ...prev,
                                      phone: "",
                                    }));
                                  }}
                                  className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-gray-50 transition-colors text-left ${
                                    selectedCountry.iso === c.iso &&
                                    selectedCountry.code === c.code
                                      ? "bg-[#00395F]/5 font-semibold text-[#00395F]"
                                      : "text-gray-700"
                                  }`}
                                >
                                  <span className="text-lg leading-none">
                                    {c.flag}
                                  </span>
                                  <span className="flex-1">{c.name}</span>
                                  <span className="text-gray-400 text-xs">
                                    {c.code}
                                  </span>
                                </button>
                              ))}
                              {filteredCountries.length === 0 && (
                                <p className="text-center py-4 text-sm text-gray-400">
                                  No results
                                </p>
                              )}
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Phone input with validation icon */}
                      <div className="relative flex-1">
                        <input
                          type="tel"
                          placeholder={`${maxPhoneLength}-digit number`}
                          value={formData.phone}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              phone: e.target.value
                                .replace(/\D/g, "")
                                .slice(0, maxPhoneLength),
                            })
                          }
                          className={`w-full px-4 py-3 pr-10 border rounded-xl text-sm focus:outline-none focus:ring-2 focus:border-transparent transition-all bg-gray-50 hover:bg-white ${
                            phoneStarted && phoneValid
                              ? "border-green-400 focus:ring-green-400"
                              : phoneStarted && !phoneValid
                                ? "border-red-300 focus:ring-red-300"
                                : "border-gray-200 focus:ring-[#00395F]"
                          }`}
                          maxLength={maxPhoneLength}
                          required
                          inputMode="numeric"
                        />
                        {phoneStarted && (
                          <div className="absolute right-3 top-1/2 -translate-y-1/2">
                            {phoneValid ? (
                              <CheckCircleIcon className="h-5 w-5 text-green-500" />
                            ) : (
                              <XCircleIcon className="h-5 w-5 text-red-400" />
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                    {phoneStarted && !phoneValid && (
                      <p className="text-xs text-red-500 mt-1.5 ml-1">
                        Enter {maxPhoneLength} digits for {selectedCountry.name}
                      </p>
                    )}
                  </div>

                  <button
                    type="submit"
                    disabled={isLoading || !phoneValid}
                    className="w-full py-3.5 rounded-xl font-semibold text-sm transition-all mt-2"
                    style={{
                      background: phoneValid ? "#00395F" : "#e5e7eb",
                      color: phoneValid ? "#fff" : "#9ca3af",
                      cursor: phoneValid ? "pointer" : "not-allowed",
                    }}
                  >
                    {isLoading ? (
                      <span className="flex items-center justify-center gap-2">
                        <svg
                          className="animate-spin h-4 w-4"
                          viewBox="0 0 24 24"
                          fill="none"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          />
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                          />
                        </svg>
                        Sending OTP...
                      </span>
                    ) : (
                      "Continue"
                    )}
                  </button>

                  <p className="text-xs text-gray-400 text-center mt-4 leading-relaxed">
                    By continuing, you agree to our{" "}
                    <span className="text-[#00395F] cursor-pointer hover:underline">
                      Terms
                    </span>{" "}
                    &amp;{" "}
                    <span className="text-[#00395F] cursor-pointer hover:underline">
                      Privacy Policy
                    </span>
                  </p>
                </form>
              )}

              {/*  STEP: OTP  */}
              {step === "otp" && (
                <form onSubmit={handleOtpSubmit}>
                  <div className="mb-6">
                    <button
                      type="button"
                      onClick={() => {
                        setStep("phone");
                        setOtp(["", "", "", "", "", ""]);
                      }}
                      className="flex items-center gap-1 text-sm text-[#00395F] font-medium mb-4 hover:underline"
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M15 19l-7-7 7-7"
                        />
                      </svg>
                      Change Number
                    </button>
                    <h2 className="text-2xl font-bold text-gray-900 mb-1">
                      Enter OTP
                    </h2>
                    <p className="text-sm text-gray-500">
                      Sent to{" "}
                      <strong className="text-gray-800">
                        {selectedCountry.code} {formData.phone}
                      </strong>
                    </p>
                    {!isNewUser && (
                      <p className="text-xs text-green-600 mt-1">
                        ✓ We'll save your guest cart items
                      </p>
                    )}
                  </div>

                  <div className="grid grid-cols-6 mb-3 gap-1.5">
                    {otp.map((digit, i) => (
                      <input
                        key={i}
                        ref={(el) => (otpRefs.current[i] = el)}
                        type="text"
                        maxLength="1"
                        value={digit}
                        onChange={(e) => handleOtpChange(i, e.target.value)}
                        onKeyDown={(e) => handleOtpKeyDown(i, e)}
                        inputMode="numeric"
                        className={`flex-1 h-12 text-center text-xl font-bold border-2 rounded-xl transition-all focus:outline-none focus:ring-2 focus:ring-[#00395F]/30 ${
                          digit
                            ? "border-[#00395F] bg-[#00395F]/5 text-[#00395F]"
                            : "border-gray-200 bg-gray-50"
                        }`}
                        aria-label={`OTP digit ${i + 1}`}
                      />
                    ))}
                  </div>

                  <button
                    type="submit"
                    disabled={isLoading || otp.join("").length < 6}
                    className="w-full py-3.5 rounded-xl font-semibold text-sm text-white transition-all"
                    style={{
                      background:
                        otp.join("").length === 6 ? "#00395F" : "#e5e7eb",
                      color: otp.join("").length === 6 ? "#fff" : "#9ca3af",
                    }}
                  >
                    {isLoading ? "Verifying..." : "Verify OTP"}
                  </button>

                  <div className="mt-4 p-3 bg-[#00395F]/5 rounded-xl text-center border border-[#00395F]/10">
                    <p className="text-xs text-[#00395F]">
                      Demo OTP: <b className="tracking-widest">111111</b>
                    </p>
                  </div>

                  <div className="mt-4 text-center">
                    <button
                      type="button"
                      className="text-sm text-gray-500 hover:text-[#00395F] transition-colors"
                    >
                      Resend OTP in{" "}
                      <span className="font-semibold text-[#00395F]">30s</span>
                    </button>
                  </div>
                </form>
              )}

              {/*  STEP: PROFILE COMPLETION  */}
              {step === "profile" && (
                <form onSubmit={handleProfileSubmit}>
                  <div className="mb-5">
                    <h2 className="text-2xl font-bold text-gray-900 mb-1">
                      Complete Profile
                    </h2>
                    <p className="text-sm text-gray-500">
                      Almost done! Fill in your details to get started.
                    </p>
                    <p className="text-xs text-green-600 mt-1">
                      ✓ Your guest cart items will be saved
                    </p>
                  </div>

                  <div className="space-y-3">
                    {/* Name row */}
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5 block">
                          First Name <span className="text-red-400">*</span>
                        </label>
                        <input
                          type="text"
                          placeholder="Rahul"
                          value={formData.firstName}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              firstName: e.target.value,
                            })
                          }
                          className={inputCls}
                          required
                        />
                      </div>
                      <div>
                        <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5 block">
                          Last Name
                        </label>
                        <input
                          type="text"
                          placeholder="Sharma"
                          value={formData.lastName}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              lastName: e.target.value,
                            })
                          }
                          className={inputCls}
                        />
                      </div>
                    </div>

                    {/* Email */}
                    <div>
                      <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5 block">
                        Email (optional)
                      </label>
                      <input
                        type="email"
                        placeholder="rahul@example.com"
                        value={formData.email}
                        onChange={(e) =>
                          setFormData({ ...formData, email: e.target.value })
                        }
                        className={inputCls}
                      />
                    </div>

                    {/* Gender */}
                    <div>
                      <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5 block">
                        Gender <span className="text-red-400">*</span>
                      </label>
                      <div className="flex gap-2">
                        {["Male", "Female", "Other"].map((g) => (
                          <button
                            key={g}
                            type="button"
                            onClick={() =>
                              setFormData({ ...formData, gender: g })
                            }
                            className={`flex-1 py-2.5 rounded-xl text-sm font-medium border transition-all ${
                              formData.gender === g
                                ? "border-[#00395F] bg-[#00395F] text-white"
                                : "border-gray-200 bg-gray-50 text-gray-600 hover:border-[#00395F]/50"
                            }`}
                          >
                            {g}
                          </button>
                        ))}
                      </div>
                    </div>
                    {/* Profile image */}
                    <div>
                      <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5 block">
                        Profile Photo (optional)
                      </label>
                      <label className="flex items-center gap-3 px-4 py-3 border-2 border-dashed border-gray-200 rounded-xl cursor-pointer hover:border-[#00395F]/40 hover:bg-[#00395F]/5 transition-all">
                        <svg
                          className="h-5 w-5 text-gray-400"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={1.5}
                            d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"
                          />
                        </svg>
                        <span className="text-sm text-gray-500">
                          {formData.image
                            ? formData.image.name
                            : "Upload photo"}
                        </span>
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              image: e.target.files[0],
                            })
                          }
                        />
                      </label>
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={isLoading}
                    className="w-full py-3.5 rounded-xl font-semibold text-sm text-white mt-5 transition-all"
                    style={{ background: "#00395F" }}
                  >
                    {isLoading ? (
                      <span className="flex items-center justify-center gap-2">
                        <svg
                          className="animate-spin h-4 w-4"
                          viewBox="0 0 24 24"
                          fill="none"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          />
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                          />
                        </svg>
                        Creating Account...
                      </span>
                    ) : (
                      "Complete Registration"
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default UserLoginModal;

// import React, { useState, useRef, useEffect } from "react";
// import {
//   XMarkIcon,
//   CheckCircleIcon,
//   XCircleIcon,
//   MapPinIcon,
// } from "@heroicons/react/24/outline";
// import axios from "axios";
// import toast, { Toaster } from "react-hot-toast";
// import Swal from "sweetalert2";
// import loginimg from "../../Assets/Login/Modal.png";
// import { uploadToCloudinary } from "../../utils/cloudinary";
// import { useTranslation } from "react-i18next";

// const DEMO_OTP = "111111";

// const COUNTRIES = [
//   { code: "+91", iso: "IN", flag: "🇮🇳", name: "India" },
//   { code: "+1", iso: "US", flag: "🇺🇸", name: "United States" },
//   { code: "+44", iso: "GB", flag: "🇬🇧", name: "United Kingdom" },
//   { code: "+61", iso: "AU", flag: "🇦🇺", name: "Australia" },
//   { code: "+971", iso: "AE", flag: "🇦🇪", name: "UAE" },
//   { code: "+65", iso: "SG", flag: "🇸🇬", name: "Singapore" },
//   { code: "+60", iso: "MY", flag: "🇲🇾", name: "Malaysia" },
//   { code: "+49", iso: "DE", flag: "🇩🇪", name: "Germany" },
//   { code: "+33", iso: "FR", flag: "🇫🇷", name: "France" },
//   { code: "+81", iso: "JP", flag: "🇯🇵", name: "Japan" },
//   { code: "+86", iso: "CN", flag: "🇨🇳", name: "China" },
//   { code: "+7", iso: "RU", flag: "🇷🇺", name: "Russia" },
//   { code: "+55", iso: "BR", flag: "🇧🇷", name: "Brazil" },
//   { code: "+27", iso: "ZA", flag: "🇿🇦", name: "South Africa" },
//   { code: "+966", iso: "SA", flag: "🇸🇦", name: "Saudi Arabia" },
//   { code: "+92", iso: "PK", flag: "🇵🇰", name: "Pakistan" },
//   { code: "+880", iso: "BD", flag: "🇧🇩", name: "Bangladesh" },
//   { code: "+94", iso: "LK", flag: "🇱🇰", name: "Sri Lanka" },
//   { code: "+977", iso: "NP", flag: "🇳🇵", name: "Nepal" },
//   { code: "+1", iso: "CA", flag: "🇨🇦", name: "Canada" },
// ];

// // Phone number max lengths by country ISO
// const PHONE_LENGTHS = {
//   IN: 10,
//   US: 10,
//   CA: 10,
//   GB: 10,
//   AU: 9,
//   AE: 9,
//   SG: 8,
//   MY: 9,
//   DE: 11,
//   FR: 9,
//   JP: 10,
//   CN: 11,
//   RU: 10,
//   BR: 11,
//   ZA: 9,
//   SA: 9,
//   PK: 10,
//   BD: 10,
//   LK: 9,
//   NP: 10,
// };

// const UserLoginModal = ({ onClose, onLoginSuccess }) => {
//   // Steps: "phone" | "otp" | "profile"
//   const [step, setStep] = useState("phone");
//   const [isLoading, setIsLoading] = useState(false);
//   const [otp, setOtp] = useState(["", "", "", "", "", ""]);
//   const [selectedCountry, setSelectedCountry] = useState(COUNTRIES[0]);
//   const [showCountryDropdown, setShowCountryDropdown] = useState(false);
//   const [countrySearch, setCountrySearch] = useState("");
//   const [isNewUser, setIsNewUser] = useState(false);
//   const [loggedInUser, setLoggedInUser] = useState(null);
//   const [locationFetching, setLocationFetching] = useState(false);

//   const otpRefs = useRef([]);
//   const modalRef = useRef(null);
//   const countryDropdownRef = useRef(null);
//   const { t } = useTranslation();

//   const maxPhoneLength = PHONE_LENGTHS[selectedCountry.iso] || 10;

//   const [formData, setFormData] = useState({
//     phone: "",
//     firstName: "",
//     lastName: "",
//     email: "",
//     gender: "",
//     image: null,
//     location: "",
//   });

//   const phoneValid = formData.phone.length === maxPhoneLength;
//   const phoneStarted = formData.phone.length > 0;

//   // Close on outside click
//   useEffect(() => {
//     const handleClickOutside = (e) => {
//       if (modalRef.current && !modalRef.current.contains(e.target)) onClose?.();
//       if (
//         countryDropdownRef.current &&
//         !countryDropdownRef.current.contains(e.target)
//       ) {
//         setShowCountryDropdown(false);
//       }
//     };
//     document.addEventListener("mousedown", handleClickOutside);
//     return () => document.removeEventListener("mousedown", handleClickOutside);
//   }, [onClose]);

//   const filteredCountries = COUNTRIES.filter(
//     (c) =>
//       c.name.toLowerCase().includes(countrySearch.toLowerCase()) ||
//       c.code.includes(countrySearch),
//   );

//   // Auto-fetch live location
//   const fetchLocation = () => {
//     if (!navigator.geolocation) {
//       toast.error("Geolocation not supported by your browser");
//       return;
//     }
//     setLocationFetching(true);
//     navigator.geolocation.getCurrentPosition(
//       async (pos) => {
//         const { latitude, longitude } = pos.coords;
//         try {
//           const res = await fetch(
//             `https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json`,
//           );
//           const data = await res.json();
//           const addr = data.address;
//           const readable = [
//             addr.suburb || addr.neighbourhood || addr.village,
//             addr.city || addr.town || addr.county,
//             addr.state,
//             addr.country,
//           ]
//             .filter(Boolean)
//             .join(", ");
//           setFormData((prev) => ({ ...prev, location: readable }));
//           toast.success("Location fetched!");
//         } catch {
//           setFormData((prev) => ({
//             ...prev,
//             location: `${latitude.toFixed(4)}, ${longitude.toFixed(4)}`,
//           }));
//         }
//         setLocationFetching(false);
//       },
//       (err) => {
//         toast.error("Could not fetch location. Please allow location access.");
//         setLocationFetching(false);
//       },
//       { timeout: 10000 },
//     );
//   };

//   // Step 1: Send OTP (Flipkart style — no registration upfront)
//   const handlePhoneSubmit = async (e) => {
//     e.preventDefault();
//     if (!phoneValid) {
//       toast.error(`Please enter a valid ${maxPhoneLength}-digit phone number`);
//       return;
//     }

//     setIsLoading(true);
//     try {
//       const fullPhone = `${selectedCountry.code}${formData.phone}`;
//       const { data } = await axios.post(
//         `${process.env.REACT_APP_API_URL}/customers/user-login-by-phone`,
//         { phone: formData.phone },
//       );

//       if (data?.success) {
//         // Existing user
//         setIsNewUser(false);
//         toast.success(t("loginModal.otp_sent") || "OTP sent! (Demo: 111111)");
//       } else {
//         // New user — still send OTP, collect profile after
//         setIsNewUser(true);
//         toast(
//           t("loginModal.new_user_otp") ||
//             "New user! OTP sent — complete profile after login.",
//         );
//       }
//       setStep("otp");
//       setTimeout(() => otpRefs.current[0]?.focus(), 300);
//     } catch (err) {
//       // If 404 it's a new user — still proceed to OTP
//       if (err?.response?.status === 404) {
//         setIsNewUser(true);
//         toast("New user detected — verify OTP to continue");
//         setStep("otp");
//         setTimeout(() => otpRefs.current[0]?.focus(), 300);
//       } else {
//         toast.error(
//           t("loginModal.login_error") || "Error — check your connection",
//         );
//       }
//     } finally {
//       setIsLoading(false);
//     }
//   };

//   // Step 2: OTP handlers
//   const handleOtpChange = (index, value) => {
//     if (value && !/^\d+$/.test(value)) return;
//     const newOtp = [...otp];
//     newOtp[index] = value.slice(0, 1);
//     setOtp(newOtp);
//     if (value && index < 5) otpRefs.current[index + 1]?.focus();
//   };

//   const handleOtpKeyDown = (index, e) => {
//     if (e.key === "Backspace" && !otp[index] && index > 0)
//       otpRefs.current[index - 1]?.focus();
//   };

//   // Step 3: Verify OTP
//   const handleOtpSubmit = async (e) => {
//     e.preventDefault();
//     const enteredOtp = otp.join("");

//     if (enteredOtp !== DEMO_OTP) {
//       toast.error("Invalid OTP (Demo: 111111)");
//       setOtp(["", "", "", "", "", ""]);
//       otpRefs.current[0]?.focus();
//       return;
//     }

//     if (isNewUser) {
//       // New user: go to profile completion
//       toast.success("OTP verified! Complete your profile.");
//       setStep("profile");
//       // Auto-fetch location for new user
//       setTimeout(() => fetchLocation(), 500);
//       return;
//     }

//     // Existing user: login directly
//     setIsLoading(true);
//     try {
//       const { data } = await axios.post(
//         `${process.env.REACT_APP_API_URL}/customers/user-login-by-phone`,
//         { phone: formData.phone },
//       );

//       if (data.success) {
//         const user = data.data;
//         localStorage.setItem("pm-user-data", JSON.stringify(user));
//         onLoginSuccess?.(user);
//         await Swal.fire({
//           icon: "success",
//           title: `Welcome back, ${user.firstName || "User"}!`,
//           confirmButtonColor: "#00395F",
//         });
//         onClose?.();
//       } else {
//         toast.error("Unable to fetch user data.");
//       }
//     } catch (err) {
//       toast.error("Login failed. Try again.");
//     } finally {
//       setIsLoading(false);
//     }
//   };

//   // Step 4: Profile completion (new users only)
//   const handleProfileSubmit = async (e) => {
//     e.preventDefault();
//     if (!formData.firstName.trim() || !formData.gender) {
//       toast.error("Please fill all required fields");
//       return;
//     }

//     setIsLoading(true);
//     try {
//       let imageUrl = "";
//       if (formData.image) {
//         const uploadRes = await uploadToCloudinary(formData.image);
//         imageUrl = uploadRes.secure_url;
//       }

//       const payload = {
//         firstName: formData.firstName.trim(),
//         lastName: formData.lastName.trim(),
//         email: formData.email.trim(),
//         gender: formData.gender,
//         phone: formData.phone,
//         imageUrl: imageUrl || "",
//       };

//       const { data } = await axios.post(
//         `${process.env.REACT_APP_API_URL}/customers/create-user`,
//         payload,
//       );

//       if (data.success) {
//         const user = data.data;
//         localStorage.setItem("pm-user-data", JSON.stringify(user));
//         onLoginSuccess?.(user);
//         await Swal.fire({
//           icon: "success",
//           title: `Welcome to Bharat Karigari, ${user.firstName}!`,
//           confirmButtonColor: "#00395F",
//         });
//         onClose?.();
//       } else {
//         toast.error(data.error || "Registration failed");
//       }
//     } catch (err) {
//       toast.error(err?.response?.data?.error || "Something went wrong");
//     } finally {
//       setIsLoading(false);
//     }
//   };

//   const inputCls =
//     "w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#00395F] focus:border-transparent transition-all bg-gray-50 hover:bg-white";

//   const stepLabels = ["Phone", "OTP", "Profile"];
//   const stepIndex = step === "phone" ? 0 : step === "otp" ? 1 : 2;

//   return (
//     <>
//       <Toaster position="top-center" />
//       <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center p-4 z-[999] backdrop-blur-sm">
//         <div
//           ref={modalRef}
//           className="bg-white rounded-3xl overflow-hidden max-w-4xl w-full shadow-2xl grid md:grid-cols-2"
//           style={{ maxHeight: "95vh" }}
//         >
//           {/*  Left: Image Panel  */}
//           <div className="relative hidden md:block">
//             <img
//               src={loginimg}
//               alt="Login Visual"
//               className="absolute inset-0 w-full h-full object-cover"
//             />
//             {/* Overlay gradient */}
//             <div className="absolute inset-0 bg-gradient-to-t from-[#00395F]/80 to-transparent" />
//             <div className="absolute bottom-8 left-8 right-8 text-white">
//               <p className="text-xs uppercase tracking-widest opacity-70 mb-1 font-medium">
//                 Bharat Karigari
//               </p>
//               <h2 className="text-3xl font-bold leading-snug">
//                 Discover Royalty
//                 <br />
//                 <span className="opacity-80 text-2xl font-normal">
//                   Exclusive Insights Await
//                 </span>
//               </h2>
//             </div>
//           </div>

//           {/*  Right: Form Panel  */}
//           <div
//             className="flex flex-col relative overflow-y-auto"
//             style={{ maxHeight: "95vh" }}
//           >
//             {/* Close button */}
//             <button
//               onClick={onClose}
//               className="absolute top-4 right-4 z-10 p-1.5 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-500 hover:text-gray-800 transition-all"
//               aria-label="Close"
//             >
//               <XMarkIcon className="h-4 w-4" />
//             </button>

//             <div className="px-8 pt-10 pb-6 flex-1">
//               {/* Step indicator */}
//               <div className="flex items-center gap-2 mb-8">
//                 {stepLabels.map((label, i) => (
//                   <React.Fragment key={label}>
//                     <div className="flex items-center gap-1.5">
//                       <div
//                         className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold transition-all ${
//                           i < stepIndex
//                             ? "bg-[#00395F] text-white"
//                             : i === stepIndex
//                               ? "bg-[#00395F] text-white ring-4 ring-[#00395F]/20"
//                               : "bg-gray-100 text-gray-400"
//                         }`}
//                       >
//                         {i < stepIndex ? "✓" : i + 1}
//                       </div>
//                       <span
//                         className={`text-xs font-medium ${
//                           i === stepIndex ? "text-[#00395F]" : "text-gray-400"
//                         }`}
//                       >
//                         {label}
//                       </span>
//                     </div>
//                     {i < stepLabels.length - 1 && (
//                       <div
//                         className={`flex-1 h-0.5 rounded transition-all ${
//                           i < stepIndex ? "bg-[#00395F]" : "bg-gray-200"
//                         }`}
//                       />
//                     )}
//                   </React.Fragment>
//                 ))}
//               </div>

//               {/*  STEP: PHONE  */}
//               {step === "phone" && (
//                 <form onSubmit={handlePhoneSubmit}>
//                   <div className="mb-6">
//                     <h2 className="text-2xl font-bold text-gray-900 mb-1">
//                       Sign in
//                     </h2>
//                     <p className="text-sm text-gray-500">
//                       Enter your phone number to continue
//                     </p>
//                   </div>

//                   {/* Country code + Phone */}
//                   <div className="mb-4">
//                     <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 block">
//                       Phone Number
//                     </label>
//                     <div className="flex gap-2">
//                       {/* Country Selector */}
//                       <div className="relative" ref={countryDropdownRef}>
//                         <button
//                           type="button"
//                           onClick={() =>
//                             setShowCountryDropdown(!showCountryDropdown)
//                           }
//                           className="flex items-center gap-2 px-3 py-3 border border-gray-200 rounded-xl bg-gray-50 hover:bg-white text-sm font-medium transition-all focus:outline-none focus:ring-2 focus:ring-[#00395F] whitespace-nowrap"
//                           style={{ minWidth: "96px" }}
//                         >
//                           <span className="text-lg leading-none">
//                             {selectedCountry.flag}
//                           </span>
//                           <span className="text-gray-700">
//                             {selectedCountry.code}
//                           </span>
//                           <svg
//                             className="w-3 h-3 text-gray-400"
//                             fill="none"
//                             stroke="currentColor"
//                             viewBox="0 0 24 24"
//                           >
//                             <path
//                               strokeLinecap="round"
//                               strokeLinejoin="round"
//                               strokeWidth={2}
//                               d="M19 9l-7 7-7-7"
//                             />
//                           </svg>
//                         </button>

//                         {showCountryDropdown && (
//                           <div className="absolute top-full left-0 mt-1 w-64 bg-white border border-gray-200 rounded-xl shadow-xl z-50 overflow-hidden">
//                             <div className="p-2 border-b border-gray-100">
//                               <input
//                                 type="text"
//                                 placeholder="Search country..."
//                                 value={countrySearch}
//                                 onChange={(e) =>
//                                   setCountrySearch(e.target.value)
//                                 }
//                                 className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00395F]"
//                                 autoFocus
//                               />
//                             </div>
//                             <div className="max-h-52 overflow-y-auto">
//                               {filteredCountries.map((c) => (
//                                 <button
//                                   key={`${c.iso}-${c.code}`}
//                                   type="button"
//                                   onClick={() => {
//                                     setSelectedCountry(c);
//                                     setShowCountryDropdown(false);
//                                     setCountrySearch("");
//                                     setFormData((prev) => ({
//                                       ...prev,
//                                       phone: "",
//                                     }));
//                                   }}
//                                   className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-gray-50 transition-colors text-left ${
//                                     selectedCountry.iso === c.iso &&
//                                     selectedCountry.code === c.code
//                                       ? "bg-[#00395F]/5 font-semibold text-[#00395F]"
//                                       : "text-gray-700"
//                                   }`}
//                                 >
//                                   <span className="text-lg leading-none">
//                                     {c.flag}
//                                   </span>
//                                   <span className="flex-1">{c.name}</span>
//                                   <span className="text-gray-400 text-xs">
//                                     {c.code}
//                                   </span>
//                                 </button>
//                               ))}
//                               {filteredCountries.length === 0 && (
//                                 <p className="text-center py-4 text-sm text-gray-400">
//                                   No results
//                                 </p>
//                               )}
//                             </div>
//                           </div>
//                         )}
//                       </div>

//                       {/* Phone input with validation icon */}
//                       <div className="relative flex-1">
//                         <input
//                           type="tel"
//                           placeholder={`${maxPhoneLength}-digit number`}
//                           value={formData.phone}
//                           onChange={(e) =>
//                             setFormData({
//                               ...formData,
//                               phone: e.target.value
//                                 .replace(/\D/g, "")
//                                 .slice(0, maxPhoneLength),
//                             })
//                           }
//                           className={`w-full px-4 py-3 pr-10 border rounded-xl text-sm focus:outline-none focus:ring-2 focus:border-transparent transition-all bg-gray-50 hover:bg-white ${
//                             phoneStarted && phoneValid
//                               ? "border-green-400 focus:ring-green-400"
//                               : phoneStarted && !phoneValid
//                                 ? "border-red-300 focus:ring-red-300"
//                                 : "border-gray-200 focus:ring-[#00395F]"
//                           }`}
//                           maxLength={maxPhoneLength}
//                           required
//                           inputMode="numeric"
//                         />
//                         {phoneStarted && (
//                           <div className="absolute right-3 top-1/2 -translate-y-1/2">
//                             {phoneValid ? (
//                               <CheckCircleIcon className="h-5 w-5 text-green-500" />
//                             ) : (
//                               <XCircleIcon className="h-5 w-5 text-red-400" />
//                             )}
//                           </div>
//                         )}
//                       </div>
//                     </div>
//                     {phoneStarted && !phoneValid && (
//                       <p className="text-xs text-red-500 mt-1.5 ml-1">
//                         Enter {maxPhoneLength} digits for {selectedCountry.name}
//                       </p>
//                     )}
//                   </div>

//                   <button
//                     type="submit"
//                     disabled={isLoading || !phoneValid}
//                     className="w-full py-3.5 rounded-xl font-semibold text-sm transition-all mt-2"
//                     style={{
//                       background: phoneValid ? "#00395F" : "#e5e7eb",
//                       color: phoneValid ? "#fff" : "#9ca3af",
//                       cursor: phoneValid ? "pointer" : "not-allowed",
//                     }}
//                   >
//                     {isLoading ? (
//                       <span className="flex items-center justify-center gap-2">
//                         <svg
//                           className="animate-spin h-4 w-4"
//                           viewBox="0 0 24 24"
//                           fill="none"
//                         >
//                           <circle
//                             className="opacity-25"
//                             cx="12"
//                             cy="12"
//                             r="10"
//                             stroke="currentColor"
//                             strokeWidth="4"
//                           />
//                           <path
//                             className="opacity-75"
//                             fill="currentColor"
//                             d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
//                           />
//                         </svg>
//                         Sending OTP...
//                       </span>
//                     ) : (
//                       "Continue"
//                     )}
//                   </button>

//                   <p className="text-xs text-gray-400 text-center mt-4 leading-relaxed">
//                     By continuing, you agree to our{" "}
//                     <span className="text-[#00395F] cursor-pointer hover:underline">
//                       Terms
//                     </span>{" "}
//                     &amp;{" "}
//                     <span className="text-[#00395F] cursor-pointer hover:underline">
//                       Privacy Policy
//                     </span>
//                   </p>
//                 </form>
//               )}

//               {/*  STEP: OTP  */}
//               {step === "otp" && (
//                 <form onSubmit={handleOtpSubmit}>
//                   <div className="mb-6">
//                     <button
//                       type="button"
//                       onClick={() => {
//                         setStep("phone");
//                         setOtp(["", "", "", "", "", ""]);
//                       }}
//                       className="flex items-center gap-1 text-sm text-[#00395F] font-medium mb-4 hover:underline"
//                     >
//                       <svg
//                         className="w-4 h-4"
//                         fill="none"
//                         stroke="currentColor"
//                         viewBox="0 0 24 24"
//                       >
//                         <path
//                           strokeLinecap="round"
//                           strokeLinejoin="round"
//                           strokeWidth={2}
//                           d="M15 19l-7-7 7-7"
//                         />
//                       </svg>
//                       Change Number
//                     </button>
//                     <h2 className="text-2xl font-bold text-gray-900 mb-1">
//                       Enter OTP
//                     </h2>
//                     <p className="text-sm text-gray-500">
//                       Sent to{" "}
//                       <strong className="text-gray-800">
//                         {selectedCountry.code} {formData.phone}
//                       </strong>
//                     </p>
//                   </div>

//                   <div className="grid grid-cols-6 mb-3 gap-1.5">
//                     {otp.map((digit, i) => (
//                       <input
//                         key={i}
//                         ref={(el) => (otpRefs.current[i] = el)}
//                         type="text"
//                         maxLength="1"
//                         value={digit}
//                         onChange={(e) => handleOtpChange(i, e.target.value)}
//                         onKeyDown={(e) => handleOtpKeyDown(i, e)}
//                         inputMode="numeric"
//                         className={`flex-1 h-12 text-center text-xl font-bold border-2 rounded-xl transition-all focus:outline-none focus:ring-2 focus:ring-[#00395F]/30 ${
//                           digit
//                             ? "border-[#00395F] bg-[#00395F]/5 text-[#00395F]"
//                             : "border-gray-200 bg-gray-50"
//                         }`}
//                         aria-label={`OTP digit ${i + 1}`}
//                       />
//                     ))}
//                   </div>

//                   <button
//                     type="submit"
//                     disabled={isLoading || otp.join("").length < 6}
//                     className="w-full py-3.5 rounded-xl font-semibold text-sm text-white transition-all"
//                     style={{
//                       background:
//                         otp.join("").length === 6 ? "#00395F" : "#e5e7eb",
//                       color: otp.join("").length === 6 ? "#fff" : "#9ca3af",
//                     }}
//                   >
//                     {isLoading ? "Verifying..." : "Verify OTP"}
//                   </button>

//                   <div className="mt-4 p-3 bg-[#00395F]/5 rounded-xl text-center border border-[#00395F]/10">
//                     <p className="text-xs text-[#00395F]">
//                       Demo OTP: <b className="tracking-widest">111111</b>
//                     </p>
//                   </div>

//                   <div className="mt-4 text-center">
//                     <button
//                       type="button"
//                       className="text-sm text-gray-500 hover:text-[#00395F] transition-colors"
//                     >
//                       Resend OTP in{" "}
//                       <span className="font-semibold text-[#00395F]">30s</span>
//                     </button>
//                   </div>
//                 </form>
//               )}

//               {/*  STEP: PROFILE COMPLETION  */}
//               {step === "profile" && (
//                 <form onSubmit={handleProfileSubmit}>
//                   <div className="mb-5">
//                     <h2 className="text-2xl font-bold text-gray-900 mb-1">
//                       Complete Profile
//                     </h2>
//                     <p className="text-sm text-gray-500">
//                       Almost done! Fill in your details to get started.
//                     </p>
//                   </div>

//                   <div className="space-y-3">
//                     {/* Name row */}
//                     <div className="grid grid-cols-2 gap-3">
//                       <div>
//                         <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5 block">
//                           First Name <span className="text-red-400">*</span>
//                         </label>
//                         <input
//                           type="text"
//                           placeholder="Rahul"
//                           value={formData.firstName}
//                           onChange={(e) =>
//                             setFormData({
//                               ...formData,
//                               firstName: e.target.value,
//                             })
//                           }
//                           className={inputCls}
//                           required
//                         />
//                       </div>
//                       <div>
//                         <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5 block">
//                           Last Name
//                         </label>
//                         <input
//                           type="text"
//                           placeholder="Sharma"
//                           value={formData.lastName}
//                           onChange={(e) =>
//                             setFormData({
//                               ...formData,
//                               lastName: e.target.value,
//                             })
//                           }
//                           className={inputCls}
//                         />
//                       </div>
//                     </div>

//                     {/* Email */}
//                     <div>
//                       <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5 block">
//                         Email (optional)
//                       </label>
//                       <input
//                         type="email"
//                         placeholder="rahul@example.com"
//                         value={formData.email}
//                         onChange={(e) =>
//                           setFormData({ ...formData, email: e.target.value })
//                         }
//                         className={inputCls}
//                       />
//                     </div>

//                     {/* Gender */}
//                     <div>
//                       <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5 block">
//                         Gender <span className="text-red-400">*</span>
//                       </label>
//                       <div className="flex gap-2">
//                         {["Male", "Female", "Other"].map((g) => (
//                           <button
//                             key={g}
//                             type="button"
//                             onClick={() =>
//                               setFormData({ ...formData, gender: g })
//                             }
//                             className={`flex-1 py-2.5 rounded-xl text-sm font-medium border transition-all ${
//                               formData.gender === g
//                                 ? "border-[#00395F] bg-[#00395F] text-white"
//                                 : "border-gray-200 bg-gray-50 text-gray-600 hover:border-[#00395F]/50"
//                             }`}
//                           >
//                             {g}
//                           </button>
//                         ))}
//                       </div>
//                     </div>
//                     {/* Profile image */}
//                     <div>
//                       <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5 block">
//                         Profile Photo (optional)
//                       </label>
//                       <label className="flex items-center gap-3 px-4 py-3 border-2 border-dashed border-gray-200 rounded-xl cursor-pointer hover:border-[#00395F]/40 hover:bg-[#00395F]/5 transition-all">
//                         <svg
//                           className="h-5 w-5 text-gray-400"
//                           fill="none"
//                           stroke="currentColor"
//                           viewBox="0 0 24 24"
//                         >
//                           <path
//                             strokeLinecap="round"
//                             strokeLinejoin="round"
//                             strokeWidth={1.5}
//                             d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"
//                           />
//                         </svg>
//                         <span className="text-sm text-gray-500">
//                           {formData.image
//                             ? formData.image.name
//                             : "Upload photo"}
//                         </span>
//                         <input
//                           type="file"
//                           accept="image/*"
//                           className="hidden"
//                           onChange={(e) =>
//                             setFormData({
//                               ...formData,
//                               image: e.target.files[0],
//                             })
//                           }
//                         />
//                       </label>
//                     </div>
//                   </div>

//                   <button
//                     type="submit"
//                     disabled={isLoading}
//                     className="w-full py-3.5 rounded-xl font-semibold text-sm text-white mt-5 transition-all"
//                     style={{ background: "#00395F" }}
//                   >
//                     {isLoading ? (
//                       <span className="flex items-center justify-center gap-2">
//                         <svg
//                           className="animate-spin h-4 w-4"
//                           viewBox="0 0 24 24"
//                           fill="none"
//                         >
//                           <circle
//                             className="opacity-25"
//                             cx="12"
//                             cy="12"
//                             r="10"
//                             stroke="currentColor"
//                             strokeWidth="4"
//                           />
//                           <path
//                             className="opacity-75"
//                             fill="currentColor"
//                             d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
//                           />
//                         </svg>
//                         Creating Account...
//                       </span>
//                     ) : (
//                       "Complete Registration"
//                     )}
//                   </button>
//                 </form>
//               )}
//             </div>
//           </div>
//         </div>
//       </div>
//     </>
//   );
// };

// export default UserLoginModal;
